/**
 * @fileoverview Types for Firstbase integration
 */

import * as yup from 'yup'

// INVENTORY ORDER STATUS ----------------------------------------------------------------------

export enum FirstbaseInventoryOrderStatus {
  ORDERED = 'ORDERED',
  PROCESSING = 'PROCESSING',
  SHIPPED = 'SHIPPED',
  CANCELLED = 'CANCELLED',
}

/** Allowed statuses for updateInventoryOrderShipmentStatus mutation (shipment-level only). */
export enum FirstbaseInventoryOrderShipmentStatus {
  ARRIVED_AT_PROCESSING_FACILITY = 'ARRIVED_AT_PROCESSING_FACILITY',
  SHIPPED = 'SHIPPED',
}

// INVENTORY DEPLOY STATUS ---------------------------------------------------------------------

export enum FirstbaseInventoryDeployStatus {
  AWAITING_ARRIVAL = 'AWAITING_ARRIVAL',
  AVAILABLE = 'AVAILABLE',
}

// INVENTORY CONDITION STATUS ------------------------------------------------------------------

export enum FirstbaseInventoryConditionStatus {
  NEW = 'NEW',
}

// INVENTORY ORDER REASON ----------------------------------------------------------------------

export enum FirstbaseInventoryOrderReason {
  ONE_OFF = 'ONE_OFF',
  RESTOCK = 'RESTOCK',
}

// MUTATION INPUT TYPES ------------------------------------------------------------------------

/**
 * Input for updating inventory order status (legacy — used by updateInventoryOrderStatus handler)
 */
export const updateInventoryOrderInputSchema = yup.object({
  inventoryOrderId: yup.string().required(),
  status: yup.string().oneOf(Object.values(FirstbaseInventoryOrderStatus)).required(),
})

export type UpdateInventoryOrderInput = yup.InferType<typeof updateInventoryOrderInputSchema>

/**
 * A single item on the updateInternalInventoryOrder mutation (UpdateInventoryOrderItem).
 */
export const updateInternalInventoryOrderItemSchema = yup.object({
  id: yup.string().nullable().optional(),
  skuId: yup.string().required(),
  pricingId: yup.string().required(),
  discountedPrice: yup.number().nullable().optional(),
  quantity: yup.number().required(),
  inventoryOrderItemType: yup.string().oneOf(['PHYSICAL', 'SERVICE']).required(),
  warrantyPricingId: yup.string().nullable().optional(),
  expectedShipDate: yup.string().nullable().optional(),
})

export type UpdateInternalInventoryOrderItemInput = yup.InferType<
  typeof updateInternalInventoryOrderItemSchema
>

/**
 * Input for the full updateInternalInventoryOrder mutation — all updatable fields.
 * Mirrors the Firstbase `UpdateInventoryOrder` GraphQL input. All fields except the
 * inventory order id are optional; only the fields provided are sent in the mutation.
 */
export const updateInternalInventoryOrderInputSchema = yup.object({
  inventoryOrderId: yup.string().required(),
  status: yup.string().oneOf(Object.values(FirstbaseInventoryOrderStatus)).nullable().optional(),
  warehouseId: yup.string().nullable().optional(),
  regionId: yup.string().nullable().optional(),
  vendorSku: yup.string().nullable().optional(),
  vendorCode: yup.string().nullable().optional(),
  quantity: yup.number().nullable().optional(),
  trackingNumber: yup.string().nullable().optional(),
  firstbaseSupplied: yup.boolean().nullable().optional(),
  notes: yup.string().nullable().optional(),
  expectedShipDate: yup.string().nullable().optional(),
  customerPoNumber: yup.string().nullable().optional(),
  billingType: yup.string().oneOf(['PURCHASED', 'SUBSCRIPTION']).nullable().optional(),
  inventoryOrderItems: yup.array().of(updateInternalInventoryOrderItemSchema).nullable().optional(),
  submittedBy: yup.string().nullable().optional(),
  reason: yup.string().oneOf(Object.values(FirstbaseInventoryOrderReason)).nullable().optional(),
})

export type UpdateInternalInventoryOrderInput = yup.InferType<
  typeof updateInternalInventoryOrderInputSchema
>

export const updateInternalInventoryOrderResponseSchema = yup.object({
  id: yup.string().required(),
  status: yup.string().required(),
})

export type UpdateInternalInventoryOrderResponse = yup.InferType<
  typeof updateInternalInventoryOrderResponseSchema
>

/**
 * Input for creating asset cost.
 * Aligned with Firstbase dev plan: amount/assetType no longer mandatory (amount from unitPrice*qty, assetType from order billing type).
 * New fields: unitPrice, inventoryOrderItemId. Required: paymentMethod, supplierId.
 */
export const createAssetCostInputSchema = yup.object({
  paymentMethod: yup.string().required('Payment method is required'),
  supplierId: yup.string().required('Supplier ID is required'),
  inventoryOrderId: yup.string().optional().nullable(),
  amount: yup.number().optional().nullable(),
  assetType: yup.string().optional().nullable(),
  unitPrice: yup.number().optional().nullable(),
  inventoryOrderItemId: yup.string().optional().nullable(),
  shippingCost: yup.number().optional().nullable(),
  taxCost: yup.number().optional().nullable(),
  currencyCode: yup.string().optional().nullable(),
  createdAt: yup.string().optional().nullable(),
  invoice: yup.string().optional().nullable(),
  proofOfPurchase: yup.string().optional().nullable(),
  supplierOrderNumber: yup.string().optional().nullable(),
  fbPurchaseOrderNumber: yup.string().optional().nullable(),
  warrantyCost: yup.number().optional().nullable(),
  warrantyTaxCost: yup.number().optional().nullable(),
  inventoryIds: yup.array().of(yup.string().required()).optional().nullable(),
})

export type CreateAssetCostInput = yup.InferType<typeof createAssetCostInputSchema>

/**
 * Input for creating inventory
 */
export const createInventoryInputSchema = yup.object({
  skuId: yup.string().required(),
  warehouseId: yup.string().required(),
  organizationSlug: yup.string().required(),
  vendorSku: yup.string().notRequired(),
  vendorCode: yup.string().notRequired(),
  firstbaseSupplied: yup.boolean().notRequired(),
  mpnValidated: yup.boolean().default(true),
  active: yup.boolean().default(true),
  conditionStatus: yup
    .string()
    .oneOf(Object.values(FirstbaseInventoryConditionStatus))
    .default(FirstbaseInventoryConditionStatus.NEW),
  deployStatus: yup
    .string()
    .oneOf(Object.values(FirstbaseInventoryDeployStatus))
    .default(FirstbaseInventoryDeployStatus.AWAITING_ARRIVAL),
  regionId: yup.string().notRequired(),
  pricingId: yup.string().notRequired(),
})

export type CreateInventoryInput = yup.InferType<typeof createInventoryInputSchema>

/**
 * Input for creating asset cost item (links inventory to asset cost)
 */
export const createAssetCostItemInputSchema = yup.object({
  assetCostId: yup.string().required(),
  inventoryId: yup.string().required(),
})

export type CreateAssetCostItemInput = yup.InferType<typeof createAssetCostItemInputSchema>

/**
 * Input for a single item in createInventoryOrderShipment
 */
export const createInventoryOrderShipmentItemInputSchema = yup.object({
  inventoryOrderItemId: yup.string().required(),
  quantity: yup.number().integer().min(1).required(),
  serialNumbers: yup.array().of(yup.string()).notRequired(),
  imei: yup.array().of(yup.array().of(yup.string())).notRequired(),
})

export type CreateInventoryOrderShipmentItemInput = yup.InferType<
  typeof createInventoryOrderShipmentItemInputSchema
>

/**
 * Input for createInventoryOrderShipment mutation
 */
export const createInventoryOrderShipmentInputSchema = yup.object({
  inventoryOrderId: yup.string().required(),
  items: yup.array().of(createInventoryOrderShipmentItemInputSchema).min(1).required(),
  shippingCarrier: yup.string().notRequired(),
  trackingCode: yup.string().notRequired(),
  trackingUrl: yup.string().notRequired(),
  estimatedDeliveryDate: yup.string().notRequired(),
  etaStart: yup.string().notRequired(),
  etaEnd: yup.string().notRequired(),
})

export type CreateInventoryOrderShipmentInput = yup.InferType<
  typeof createInventoryOrderShipmentInputSchema
>

// MUTATION RESPONSE TYPES ---------------------------------------------------------------------

/**
 * Response from update inventory order mutation
 */
export const updateInventoryOrderResponseSchema = yup.object({
  id: yup.string().required(),
  status: yup.string().required(),
})

export type UpdateInventoryOrderResponse = yup.InferType<typeof updateInventoryOrderResponseSchema>

/**
 * Response from create asset cost mutation
 */
export const createAssetCostResponseSchema = yup.object({
  id: yup.string().required(),
  fbPurchaseOrderNumber: yup.string().optional().nullable(),
})

export type CreateAssetCostResponse = yup.InferType<typeof createAssetCostResponseSchema>

/**
 * Input for modifyAssetCostById mutation
 */
export const modifyAssetCostInputSchema = yup.object({
  amount: yup.number().optional().nullable(),
  shippingCost: yup.number().optional().nullable(),
  taxCost: yup.number().optional().nullable(),
  currencyCode: yup.string().optional().nullable(),
  assetType: yup.string().optional().nullable(),
  paymentMethod: yup.string().optional().nullable(),
  createdAt: yup.string().optional().nullable(),
  inventoryOrderId: yup.string().optional().nullable(),
  invoice: yup.string().optional().nullable(),
  proofOfPurchase: yup.string().optional().nullable(),
  supplierId: yup.string().optional().nullable(),
  supplierOrderNumber: yup.string().optional().nullable(),
  fbPurchaseOrderNumber: yup.string().optional().nullable(),
  warrantyCost: yup.number().optional().nullable(),
  warrantyTaxCost: yup.number().optional().nullable(),
  unitPrice: yup.number().optional().nullable(),
})

export type ModifyAssetCostInput = yup.InferType<typeof modifyAssetCostInputSchema>

/**
 * Response from modifyAssetCostById mutation
 */
export const modifyAssetCostResponseSchema = yup.object({
  id: yup.string().required(),
})

export type ModifyAssetCostResponse = yup.InferType<typeof modifyAssetCostResponseSchema>

/**
 * Response from create inventory mutation
 */
export const createInventoryResponseSchema = yup.object({
  id: yup.string().required(),
})

export type CreateInventoryResponse = yup.InferType<typeof createInventoryResponseSchema>

/**
 * Response from create asset cost item mutation
 */
export const createAssetCostItemResponseSchema = yup.object({
  id: yup.string().required(),
})

export type CreateAssetCostItemResponse = yup.InferType<typeof createAssetCostItemResponseSchema>

/**
 * Single shipment item returned by createInventoryOrderShipment.
 */
export const createInventoryOrderShipmentItemResponseSchema = yup.object({
  id: yup.string().required(),
  quantity: yup.number().integer().required(),
  status: yup.string().required(),
  inventoryOrderItem: yup.object({ id: yup.string().required() }).required(),
})

export type CreateInventoryOrderShipmentItemResponse = yup.InferType<
  typeof createInventoryOrderShipmentItemResponseSchema
>

/**
 * Response from createInventoryOrderShipment / updateInventoryOrderShipmentTrackingInfo.
 * inventoryOrderShipmentItems is only populated by createInventoryOrderShipment, whose selection
 * set requests it; other mutations sharing this schema only select id.
 */
export const createInventoryOrderShipmentResponseSchema = yup.object({
  id: yup.string().required(),
  inventoryOrderShipmentItems: yup
    .array()
    .of(createInventoryOrderShipmentItemResponseSchema)
    .nullable()
    .optional(),
})

export type CreateInventoryOrderShipmentResponse = yup.InferType<
  typeof createInventoryOrderShipmentResponseSchema
>

// RECEIVE INVENTORY ORDER SHIPMENT ITEMS (dev plan: Receive inventory order shipment items) -----

/**
 * Per-inventory update for receiveInventoryOrderShipmentItems (id + optional serialNumber).
 */
export const updateInventoryOrderShipmentItemInventorySchema = yup.object({
  id: yup.string().required(),
  serialNumber: yup.string().optional().nullable(),
})

export type UpdateInventoryOrderShipmentItemInventory = yup.InferType<
  typeof updateInventoryOrderShipmentItemInventorySchema
>

/**
 * Optional asset cost when receiving a shipment item. unitPrice and inventoryOrderItemId are
 * required by Firstbase's backend service validation (not enforced in their GraphQL schema, to
 * stay backward-compatible with the order-level createAssetCost mutation).
 */
export const receiveInventoryOrderShipmentItemAssetCostSchema = yup.object({
  unitPrice: yup.number().required('Unit price is required'),
  inventoryOrderItemId: yup.string().required('Inventory order item ID is required'),
  paymentMethod: yup.string().required(),
  supplierId: yup.string().required(),
  amount: yup.number().optional().nullable(),
  shippingCost: yup.number().optional().nullable(),
  taxCost: yup.number().optional().nullable(),
  currencyCode: yup.string().optional().nullable(),
  assetType: yup.string().optional().nullable(),
  invoice: yup.string().optional().nullable(),
  proofOfPurchase: yup.string().optional().nullable(),
  supplierOrderNumber: yup.string().optional().nullable(),
  fbPurchaseOrderNumber: yup.string().optional().nullable(),
  warrantyCost: yup.number().optional().nullable(),
  warrantyTaxCost: yup.number().optional().nullable(),
})

export type ReceiveInventoryOrderShipmentItemAssetCost = yup.InferType<
  typeof receiveInventoryOrderShipmentItemAssetCostSchema
>

/**
 * Single item input for receiveInventoryOrderShipmentItems (shipment item id, quantity, inventories, optional assetCost).
 */
export const receiveInventoryOrderShipmentItemInputSchema = yup.object({
  id: yup.string().required(),
  quantity: yup.number().integer().min(1).required(),
  inventories: yup.array().of(updateInventoryOrderShipmentItemInventorySchema).min(1).required(),
  assetCost: receiveInventoryOrderShipmentItemAssetCostSchema.optional().nullable(),
})

export type ReceiveInventoryOrderShipmentItemInput = yup.InferType<
  typeof receiveInventoryOrderShipmentItemInputSchema
>

export const receiveInventoryOrderShipmentItemsInputSchema = yup
  .array()
  .of(receiveInventoryOrderShipmentItemInputSchema)
  .min(1)
  .required()

export type ReceiveInventoryOrderShipmentItemsInput = yup.InferType<
  typeof receiveInventoryOrderShipmentItemsInputSchema
>

/**
 * Single shipment item in receiveInventoryOrderShipmentItems response.
 */
export const inventoryOrderShipmentItemResponseSchema = yup.object({
  id: yup.string().required(),
})

export type InventoryOrderShipmentItemResponse = yup.InferType<
  typeof inventoryOrderShipmentItemResponseSchema
>

export const receiveInventoryOrderShipmentItemsResponseSchema = yup
  .array()
  .of(inventoryOrderShipmentItemResponseSchema)
  .required()

export type ReceiveInventoryOrderShipmentItemsResponse = yup.InferType<
  typeof receiveInventoryOrderShipmentItemsResponseSchema
>

// INVENTORY ORDER ITEM STATUS (new granular model from dev plan) --------------------------------

export enum InventoryOrderItemStatus {
  PENDING = 'PENDING',
  ORDERED = 'ORDERED',
  PARTIALLY_SHIPPED = 'PARTIALLY_SHIPPED',
  SHIPPED = 'SHIPPED',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
  ARRIVED_AT_PROCESSING_FACILITY = 'ARRIVED_AT_PROCESSING_FACILITY',
  PARTIALLY_RECEIVED = 'PARTIALLY_RECEIVED',
  RECEIVED = 'RECEIVED',
  CANCELLED = 'CANCELLED',
  DELIVERY_EXCEPTION = 'DELIVERY_EXCEPTION',
}

export enum InventoryOrderItemType {
  PHYSICAL = 'PHYSICAL',
  SERVICE = 'SERVICE',
}

// GET INVENTORY ORDER -------------------------------------------------------------------------

/**
 * Response for getInventoryOrder query — nested item / shipment / shipment-item data.
 * Only fields DOSS needs are included; additional fields are passed through as unknown.
 */
export const inventoryOrderItemResponseSchema = yup.object({
  id: yup.string().required(),
  status: yup.string().required(),
  quantity: yup.number().integer().required(),
  type: yup.string().nullable().optional(),
  expectedShipDate: yup.string().nullable().optional(),
  discountedPrice: yup.number().nullable().optional(),
  sku: yup
    .object({
      id: yup.string().required(),
      vendorSku: yup.string().nullable().optional(),
      alternativeMpn: yup.string().nullable().optional(),
      firstbasePartNumber: yup.string().nullable().optional(),
      skuInformation: yup
        .object({ productTitle: yup.string().nullable().optional() })
        .nullable()
        .optional(),
      vendor: yup
        .object({
          id: yup.string().nullable().optional(),
          code: yup.string().nullable().optional(),
          name: yup.string().nullable().optional(),
        })
        .nullable()
        .optional(),
      category: yup
        .object({
          id: yup.string().nullable().optional(),
          nameSingular: yup.string().nullable().optional(),
          namePlural: yup.string().nullable().optional(),
          code: yup.string().nullable().optional(),
        })
        .nullable()
        .optional(),
    })
    .required(),
  pricing: yup
    .object({
      id: yup.string().required(),
      regionCode: yup.string().nullable().optional(),
      priceOption: yup.string().nullable().optional(),
      price: yup.number().nullable().optional(),
      currencyCode: yup.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  warranty: yup
    .object({
      id: yup.string().nullable().optional(),
      status: yup.string().nullable().optional(),
      quantity: yup.number().integer().nullable().optional(),
      type: yup.string().nullable().optional(),
      expectedShipDate: yup.string().nullable().optional(),
      discountedPrice: yup.number().nullable().optional(),
      sku: yup
        .object({
          id: yup.string().nullable().optional(),
          vendorSku: yup.string().nullable().optional(),
          alternativeMpn: yup.string().nullable().optional(),
          firstbasePartNumber: yup.string().nullable().optional(),
          skuInformation: yup
            .object({ productTitle: yup.string().nullable().optional() })
            .nullable()
            .optional(),
          vendor: yup
            .object({
              id: yup.string().nullable().optional(),
              code: yup.string().nullable().optional(),
              name: yup.string().nullable().optional(),
            })
            .nullable()
            .optional(),
          category: yup
            .object({
              id: yup.string().nullable().optional(),
              nameSingular: yup.string().nullable().optional(),
              namePlural: yup.string().nullable().optional(),
              code: yup.string().nullable().optional(),
            })
            .nullable()
            .optional(),
        })
        .nullable()
        .optional(),
      pricing: yup
        .object({
          id: yup.string().nullable().optional(),
          regionCode: yup.string().nullable().optional(),
          priceOption: yup.string().nullable().optional(),
          price: yup.number().nullable().optional(),
          currencyCode: yup.string().nullable().optional(),
        })
        .nullable()
        .optional(),
      warrantyDetail: yup
        .object({
          name: yup.string().nullable().optional(),
          tier: yup.string().nullable().optional(),
          term: yup.string().nullable().optional(),
          region: yup.string().nullable().optional(),
        })
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
})

export type InventoryOrderItemResponse = yup.InferType<typeof inventoryOrderItemResponseSchema>

export const inventoryOrderShipmentItemDetailSchema = yup.object({
  id: yup.string().required(),
  quantity: yup.number().integer().required(),
  status: yup.string().required(),
  inventoryOrderItem: yup.object({ id: yup.string().required() }).required(),
  inventories: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required(),
        serialNumber: yup.string().nullable().optional(),
      }),
    )
    .nullable()
    .optional()
    .default([]),
})

export type InventoryOrderShipmentItemDetail = yup.InferType<
  typeof inventoryOrderShipmentItemDetailSchema
>

export const inventoryOrderShipmentResponseSchema = yup.object({
  id: yup.string().required(),
  status: yup.string().required(),
  shippingCarrier: yup.string().nullable().optional(),
  trackingCode: yup.string().nullable().optional(),
  trackingUrl: yup.string().nullable().optional(),
  trackingStatus: yup.string().nullable().optional(),
  trackingShipmentId: yup.string().nullable().optional(),
  estimatedDeliveryDate: yup.string().nullable().optional(),
  inventoryOrderShipmentItems: yup
    .array()
    .of(inventoryOrderShipmentItemDetailSchema)
    .nullable()
    .optional()
    .default([]),
})

export type InventoryOrderShipmentResponse = yup.InferType<
  typeof inventoryOrderShipmentResponseSchema
>

export const getInventoryOrderResponseSchema = yup.object({
  id: yup.string().required(),
  status: yup.string().required(),
  friendlyOrderId: yup.string().nullable().optional(),
  customerPoNumber: yup.string().nullable().optional(),
  billingType: yup.string().nullable().optional(),
  reason: yup.string().oneOf(Object.values(FirstbaseInventoryOrderReason)).nullable().optional(),
  submittedBy: yup.string().nullable().optional(),
  organization: yup
    .object({
      id: yup.string().nullable().optional(),
      name: yup.string().nullable().optional(),
      slug: yup.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  warehouse: yup
    .object({
      id: yup.string().nullable().optional(),
      name: yup.string().nullable().optional(),
      code: yup.string().nullable().optional(),
      address: yup
        .object({
          id: yup.string().nullable().optional(),
          addressLine1: yup.string().nullable().optional(),
          addressLine2: yup.string().nullable().optional(),
          administrativeArea: yup.string().nullable().optional(),
          countryCode: yup.string().nullable().optional(),
          locality: yup.string().nullable().optional(),
          postalCode: yup.string().nullable().optional(),
        })
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
  region: yup
    .object({
      id: yup.string().nullable().optional(),
      regionCode: yup.string().nullable().optional(),
      name: yup.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  inventoryOrderItems: yup
    .array()
    .of(inventoryOrderItemResponseSchema)
    .nullable()
    .optional()
    .default([]),
  inventoryOrderShipments: yup
    .array()
    .of(inventoryOrderShipmentResponseSchema)
    .nullable()
    .optional()
    .default([]),
})

export type GetInventoryOrderResponse = yup.InferType<typeof getInventoryOrderResponseSchema>

// UPDATE INVENTORY ORDER ITEM STATUS ----------------------------------------------------------

/**
 * Input for updateInventoryOrderItemStatus mutation.
 * Only ORDERED can be set manually per the dev plan.
 * Sets parent inventory order to PROCESSING and updates associated warranties to ORDERED.
 */
export const updateInventoryOrderItemStatusInputSchema = yup.object({
  id: yup.string().required(),
  status: yup.string().oneOf(Object.values(InventoryOrderItemStatus)).required(),
})

export type UpdateInventoryOrderItemStatusInput = yup.InferType<
  typeof updateInventoryOrderItemStatusInputSchema
>

export const updateInventoryOrderItemStatusResponseSchema = yup.object({
  id: yup.string().required(),
  status: yup.string().required(),
})

export type UpdateInventoryOrderItemStatusResponse = yup.InferType<
  typeof updateInventoryOrderItemStatusResponseSchema
>

// UPDATE INVENTORY ORDER ITEM QUANTITY --------------------------------------------------------

/**
 * Input for updateInventoryOrderItemQuantity mutation.
 * Only valid for PARTIALLY_RECEIVED items; quantity must be >= received shipment items count.
 */
export const updateInventoryOrderItemQuantityInputSchema = yup.object({
  id: yup.string().required(),
  quantity: yup.number().integer().min(1).required(),
})

export type UpdateInventoryOrderItemQuantityInput = yup.InferType<
  typeof updateInventoryOrderItemQuantityInputSchema
>

export const updateInventoryOrderItemQuantityResponseSchema = yup.object({
  id: yup.string().required(),
  quantity: yup.number().integer().required(),
  status: yup.string().required(),
})

export type UpdateInventoryOrderItemQuantityResponse = yup.InferType<
  typeof updateInventoryOrderItemQuantityResponseSchema
>

// UPDATE INVENTORY ORDER ITEM EXPECTED SHIP DATE ---------------------------------------------

/**
 * Input for updateInventoryOrderItemExpectedShipDate.
 * `expectedShipDate` is the GraphQL Date scalar (YYYY-MM-DD). The item must already be
 * PENDING or ORDERED; that status is a Firstbase precondition, not a mutation argument.
 */
export const updateInventoryOrderItemExpectedShipDateInputSchema = yup.object({
  id: yup.string().required(),
  expectedShipDate: yup
    .string()
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'expectedShipDate must be YYYY-MM-DD')
    .required(),
})

export type UpdateInventoryOrderItemExpectedShipDateInput = yup.InferType<
  typeof updateInventoryOrderItemExpectedShipDateInputSchema
>

export const updateInventoryOrderItemExpectedShipDateResponseSchema = yup.object({
  id: yup.string().required(),
  expectedShipDate: yup.string().required(),
})

export type UpdateInventoryOrderItemExpectedShipDateResponse = yup.InferType<
  typeof updateInventoryOrderItemExpectedShipDateResponseSchema
>

// GET ASSET COST BY INVENTORY ORDER ID --------------------------------------------------------

/**
 * Single asset cost record from getAssetCostByInventoryOrderId, used to resolve the Asset Cost
 * ID by matching inventoryOrderItem.id before calling modifyAssetCostById.
 */
export const assetCostByInventoryOrderIdItemSchema = yup.object({
  id: yup.string().required(),
  fbPurchaseOrderNumber: yup.string().nullable().optional(),
  inventoryOrderItem: yup.object({ id: yup.string().required() }).required(),
})

export type AssetCostByInventoryOrderIdItem = yup.InferType<
  typeof assetCostByInventoryOrderIdItemSchema
>

export const getAssetCostByInventoryOrderIdResponseSchema = yup
  .array()
  .of(assetCostByInventoryOrderIdItemSchema)
  .required()

export type GetAssetCostByInventoryOrderIdResponse = yup.InferType<
  typeof getAssetCostByInventoryOrderIdResponseSchema
>

// GET ALL SUPPLIERS ---------------------------------------------------------------------------

/**
 * Single supplier from getAllSuppliers. The Firstbase SupplierDTO exposes only id and name
 * (confirmed via GraphQL introspection).
 */
export const firstbaseSupplierSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().required(),
})

export type FirstbaseSupplier = yup.InferType<typeof firstbaseSupplierSchema>

export const getSuppliersResponseSchema = yup.array().of(firstbaseSupplierSchema).required()

export type GetSuppliersResponse = yup.InferType<typeof getSuppliersResponseSchema>
